// Student- and teacher-facing names for the IELTS question types.
//
// Three different strings are needed and they are genuinely different jobs:
//
//   officialTypeLabel  — what the printed test calls it ("Identifying
//                        Information (True/False/Not Given)"). Correct, and
//                        unscannable as a heading in a grid of nineteen cards.
//   shortTypeLabel     — the chip/heading form ("True / False / Not Given").
//   typeBlurb          — one line telling a student what the task actually
//                        asks. `question_types.description` exists but is
//                        developer-facing ("Three-way classification.") and is
//                        not reliably seeded for the codes added after the
//                        original twelve, so this is authored here instead.
//
// Lives in `shared/` because both the institute builder and the student library
// need it, and a feature may not import a sibling feature.

import { GROUP_TYPE_SPECS, type GroupTypeSpec } from './ielts-taxonomy';

// The taxonomy keys its map on the IeltsGroupType union, but every code that
// reaches a UI arrives as a plain string from an RPC or from route state. One
// widening here beats a cast at each call site.
const SPEC_BY_CODE = GROUP_TYPE_SPECS as ReadonlyMap<string, GroupTypeSpec>;

const SHORT_LABEL: ReadonlyMap<string, string> = new Map([
  ['tfng', 'True / False / Not Given'],
  ['ynng', 'Yes / No / Not Given'],
  ['mcq', 'Multiple choice'],
  ['mcq_multi', 'Choose TWO or THREE'],
  ['matching_headings', 'Matching headings'],
  ['matching_info', 'Matching information'],
  ['matching_features', 'Matching features'],
  ['matching_sentence_endings', 'Matching sentence endings'],
  ['classification', 'Classification'],
  ['sentence_completion', 'Sentence completion'],
  ['note_completion', 'Note completion'],
  ['summary_completion', 'Summary completion'],
  ['summary_completion_list', 'Summary completion (from a list)'],
  ['table_completion', 'Table completion'],
  ['flow_chart_completion', 'Flow-chart completion'],
  ['flow_chart_completion_list', 'Flow-chart completion (from a list)'],
  ['form_completion', 'Form completion'],
  ['diagram_label', 'Diagram labelling'],
  ['map_plan_label', 'Map / plan labelling'],
  ['short_answer', 'Short answer'],
]);

const BLURB: ReadonlyMap<string, string> = new Map([
  [
    'tfng',
    'Decide whether a statement matches the passage, contradicts it — or simply is not there.',
  ],
  [
    'ynng',
    "Decide whether a claim matches the writer's own views, contradicts them, or is not stated.",
  ],
  ['mcq', 'Pick the one option the passage actually supports, not the one that sounds right.'],
  ['mcq_multi', 'Pick the required number of letters. Each printed number is one mark.'],
  [
    'matching_headings',
    'Give each paragraph the heading that captures its main idea, not a detail in it.',
  ],
  ['matching_info', 'Find which paragraph contains a specific piece of information.'],
  ['matching_features', 'Match each statement to the person, place or study it belongs to.'],
  ['matching_sentence_endings', 'Complete each sentence with the ending the passage supports.'],
  ['classification', 'Sort each statement into one of a few lettered categories.'],
  ['sentence_completion', 'Complete the sentence with words taken straight from the text.'],
  ['note_completion', 'Fill the gaps in a set of notes, within the printed word limit.'],
  ['summary_completion', 'Fill the gaps in a summary using words from the passage.'],
  ['summary_completion_list', 'Fill the gaps in a summary using the lettered word list.'],
  ['table_completion', 'Fill the gaps in a table, within the printed word limit.'],
  ['flow_chart_completion', 'Fill the gaps in a flow-chart that follows a process in order.'],
  ['flow_chart_completion_list', 'Fill the flow-chart gaps using the lettered list.'],
  ['form_completion', 'Fill the gaps in a form — names, numbers, dates, spellings.'],
  ['diagram_label', 'Label the parts of a diagram with words from the text.'],
  ['map_plan_label', 'Match the lettered places on a map or plan to their names.'],
  ['short_answer', 'Answer a direct question in a few words from the text.'],
]);

/**
 * Writing tasks and Speaking parts as practice-library "types" (Phase 170).
 *
 * These rows do not decompose by IELTS question type — every one is a
 * short_answer placeholder keyed by payload.task_index / part_index — so the
 * library keys them by a synthetic code minted in three places that must
 * agree: institute_browse_question_groups (SQL), deriveTypeCodes in
 * functions/_shared/practice-set-config.ts, and here.
 */
const PRACTICE_TASK_CODES: ReadonlyMap<string, { skill: string; label: string; blurb: string }> =
  new Map([
    [
      'writing_task1',
      {
        skill: 'writing',
        label: 'Writing Task 1',
        blurb: 'Describe a chart, table, map or process in at least 150 words — about 20 minutes.',
      },
    ],
    [
      'writing_task2',
      {
        skill: 'writing',
        label: 'Writing Task 2',
        blurb: 'Argue a position on an issue in at least 250 words — about 40 minutes.',
      },
    ],
    [
      'speaking_part1',
      {
        skill: 'speaking',
        label: 'Speaking Part 1',
        blurb: 'Short questions about yourself — home, work, study, interests.',
      },
    ],
    [
      'speaking_part2',
      {
        skill: 'speaking',
        label: 'Speaking Part 2',
        blurb: 'One cue card: a minute to prepare, then talk for up to two minutes.',
      },
    ],
    [
      'speaking_part3',
      {
        skill: 'speaking',
        label: 'Speaking Part 3',
        blurb: 'Discussion questions that go deeper into the Part 2 topic.',
      },
    ],
  ]);

/** True for a Writing task / Speaking part code (see PRACTICE_TASK_CODES). */
export function isPracticeTaskCode(code: string): boolean {
  return PRACTICE_TASK_CODES.has(code);
}

function titleCase(code: string): string {
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The heading/chip form. Falls back to a title-cased code so an unmapped type
 *  still reads sensibly rather than showing raw snake_case. */
export function shortTypeLabel(code: string): string {
  return SHORT_LABEL.get(code) ?? PRACTICE_TASK_CODES.get(code)?.label ?? titleCase(code);
}

/** What the printed test calls it. */
export function officialTypeLabel(code: string): string {
  return SPEC_BY_CODE.get(code)?.officialName ?? shortTypeLabel(code);
}

/** One line on what the task asks, or null when we have nothing honest to say. */
export function typeBlurb(code: string): string | null {
  return BLURB.get(code) ?? PRACTICE_TASK_CODES.get(code)?.blurb ?? null;
}

/** The types that appear in a skill, from the taxonomy's own `skills` field —
 *  never from `question_types.skill`, which is NULL for every IELTS code on
 *  purpose because the completion and matching families are shared between
 *  Reading and Listening. */
export function typeCodesForSkill(skill: string): string[] {
  const out: string[] = [];
  for (const [code, spec] of SPEC_BY_CODE) {
    if ((spec.skills as readonly string[]).includes(skill)) out.push(code);
  }
  // Writing and Speaking have no taxonomy entries — their "types" are tasks
  // and parts (Phase 170).
  for (const [code, task] of PRACTICE_TASK_CODES) {
    if (task.skill === skill) out.push(code);
  }
  return out;
}
